import BuilderTable from './builder';
import { getDataURLFromBlob } from "../../../services/helpers";
import {
  CELL_ANGLE_FITTED_RULE,
  CELL_ANGLE_FIT_HEIGHT_RULE,
  CELL_ANGLE_FIT_WIDTH_RULE,
  CELL_ANGLE_STYLE_RULE,
  DEFAULT_CELL_HORIZONTAL_ALIGN,
  DEFAULT_CELL_VERTICAL_ALIGN,
} from "./constants";

const CACHE_FONT_DATA_URL = new Map(); // key -> Promise<fontInfoWithUrl>
// Each @font-face rule embeds the whole base64 font. getTableFonts() runs ~3x per table
// render (two measurements + the SVG), so without this every pass rebuilt hundreds of KB
// of identical string. Font files never change at runtime. POR-13578.
const CACHE_FONT_FACE_CSS = new Map(); // fontKeys -> Promise<css>

export async function getFontFaceStyle(fonts = []) {
  const fontFamilyManager = window.fontFamilyManager;
  if (!fontFamilyManager) return '';

  const cacheKey = fonts
    .map(font => (typeof font === 'object' ? (font?.index ?? font?.id ?? font?.file) : font))
    .join('|');

  const cached = CACHE_FONT_FACE_CSS.get(cacheKey);
  if (cached) {
    return cached;
  }

  const cssPromise = buildFontFaceStyle(fonts, fontFamilyManager);
  cssPromise.catch(() => CACHE_FONT_FACE_CSS.delete(cacheKey));
  CACHE_FONT_FACE_CSS.set(cacheKey, cssPromise);

  return cssPromise;
}

async function buildFontFaceStyle(fonts, fontFamilyManager) {
  const rules = await Promise.all(fonts.map(async (font) => {
    const key = typeof font === 'object' ? (font.index ?? font.id ?? font.file) : font;
    if (key == null) return '';

    let p = CACHE_FONT_DATA_URL.get(key);

    if (!p) {
      p = (async () => {
        const fontInfo = fontFamilyManager.getFontByIndex(key);
        if (!fontInfo) return null;

        const res = await fetch(`/fonts/${fontInfo.file}`, { cache: 'force-cache' });
        const blob = await res.blob();
        const url = await getDataURLFromBlob(blob);

        return { ...fontInfo, url };
      })();

      CACHE_FONT_DATA_URL.set(key, p);
    }

    const fontInfo = await p;
    if (!fontInfo?.url) return '';

    return `@font-face{ font-family:${fontInfo.index}; font-weight:${fontInfo.weight}; src:url("${fontInfo.url}"); font-display:swap; }`;
  }));

  return rules.filter(Boolean).join('\n');
}


function initDataBuilderCells(cells = []) {
  if (!Array.isArray(cells)) return [] ;
  return cells.map(_cell => {
    const cell = new BuilderTable.TableCell();
    const styles = new BuilderTable.Styles();

    styles.setRules(_cell.styles.rules);
    
    if (!_cell.styles.rules?.['white-space']) {
      styles.setRule('white-space', 'pre-line');
    }
    let value = _cell.valueHtml ? _cell.valueHtml : _cell.value;

    cell.setId(_cell.id);
    cell.setStyles(styles);
    cell.setValue(value);
    cell.setSrc(_cell.src);
    cell.setLink(_cell.link);
    cell.setMappingName(_cell.mappingName);
    cell.setColspan(_cell.colspan);
    cell.setDotLeaderEnabled(_cell.dotLeaderEnabled);
    cell.position = _cell.position === 'bottom' ? 'bottom' : 'top';
    if (Number.isFinite(_cell.staticRowIndex)) cell.staticRowIndex = _cell.staticRowIndex;
    if (Number.isFinite(_cell.afterRowIndex)) cell.afterRowIndex = _cell.afterRowIndex;
    if (_cell.rowspan && _cell.rowspan > 1) cell.setRowspan(_cell.rowspan);

    return cell;
  });
}

export function initDataBuilderTable(data) {
  if (!data || Object.keys(data || {}).length === 0) {
    const table = new BuilderTable.Table(true);

    // Only a table being created now: every stored one keeps the CSS default it was built with.
    table.setBorderBoxWidths(true);

    return table;
  }

  const table = new BuilderTable.Table();
  const styles = new BuilderTable.Styles();
  const isVisible = data.headerVisible === undefined ? true : data.headerVisible;

  data.styles.rules.padding = '0'; //there's no need padding for tables
  styles.setRules(data.styles.rules);

  table.setId(data.id);
  table.setStyles(styles);
  table.setHeaderStyles(data.headerRowStyles?.rules);

  const headers = initDataBuilderCells(data.headers);
  let staticHeaders = [];
  let staticFooters = [];

  if (Array.isArray(data.staticRows) && data.staticRows.length) {
    const staticRows = initDataBuilderCells(data.staticRows);
    staticHeaders = staticRows.filter(cell => (cell.position || 'top') === 'top');
    staticFooters = staticRows.filter(cell => (cell.position || 'top') === 'bottom');
  } else {
    staticHeaders = initDataBuilderCells(data.staticHeaders);
    staticFooters = initDataBuilderCells(data.staticFooters);
  }
  const autoFontSize = data.autoFontSize ? data.autoFontSize : false;
  const autoFontSizeValue = data.autoFontSizeValue ? data.autoFontSizeValue : null;
  const tableColumnMode = data.tableColumnMode ? data.tableColumnMode : false;
  const mergeSameValues = data.mergeSameValues ? data.mergeSameValues : false;
  const truncateLongText = data.truncateLongText ? data.truncateLongText : false;
  const borderBoxWidths = data.borderBoxWidths ? data.borderBoxWidths : false;
  const flowColumnGroups = data.flowColumnGroups ? data.flowColumnGroups : 1;

  if (!Array.isArray(data.rows)) return table;
  const rows = data.rows.map(_row => {
    const row = new BuilderTable.TableRow();
    const rowStyles = Array.isArray(_row.styles.rules) ? {} : _row.styles.rules;
    const styles = new BuilderTable.Styles(rowStyles);

    styles.setRules(styles.rules);

    row.setId(_row.id);
    row.setStyles(styles);
    row.setCells(initDataBuilderCells(_row.cells));

    return row;
  });

  table.setHeaders(headers);
  table.setStaticHeaders(staticHeaders);
  table.setStaticFooters(staticFooters);
  table.setRows(rows);
  const defaultCellPaddingShorthand = (data.defaultCellPaddingShorthand ?? "2 5").trim();
  table.setDefaultCellPaddingShorthand(defaultCellPaddingShorthand);
  table.changeTablePaddingFromShorthand(defaultCellPaddingShorthand)
  table.setAutoFontSize(autoFontSize);
  table.setAutoFontSizeValue(autoFontSizeValue);
  table.setTableColumnMode(tableColumnMode);
  table.setHeaderVisibility(isVisible);
  table.setMergeSameValues(mergeSameValues);
  table.setTruncateLongText(truncateLongText);
  table.setBorderBoxWidths(borderBoxWidths);
  table.setFlowColumnGroups(flowColumnGroups);

  fitTableCellsWithAngle(table);

  return table;
}

export function groupConnectedCells(rows, selectedCellsInRows) {
  const getKey = (row, col) => `${row}-${col}`;
  const selectedKeys = new Set();
  const visited = new Set();

  // Create a set of all keys of the selected cells
  selectedCellsInRows.forEach((row) => {
    row.forEach((cell) => {
      rows.forEach((r, ri) => {
        const colIndex = r.findIndex((c) => c.id === cell.id);
        
        if (colIndex !== -1) {
          selectedKeys.add(getKey(ri, colIndex));
        }
      });
    });
  });

  const groups = [];

  function dfs(row, col, group) {
    const key = getKey(row, col);
    if (!selectedKeys.has(key) || visited.has(key)) return;

    visited.add(key);
    group.push([row, col]); // Store cell position

    // Check neighbor cells
    const directions = [
      [0, 1],  // Left
      [0, -1], // Right
      [1, 0],  // Down
      [-1, 0]  // Up
    ];

    directions.forEach(([dr, dc]) => {
      const newRow = row + dr;
      const newCol = col + dc;
      const newKey = getKey(newRow, newCol);
      
      if (rows[newRow] && rows[newRow][newCol] && selectedKeys.has(newKey) && !visited.has(newKey)) {
        dfs(newRow, newCol, group);
      }
    });
  }

  rows.forEach((row, rowIndex) => {
    row.forEach((cell, colIndex) => {
      const key = getKey(rowIndex, colIndex);
      
      if (selectedKeys.has(key) && !visited.has(key)) {
        const group = [];
        dfs(rowIndex, colIndex, group);
        groups.push(group);
      }
    });
  });


  // Group the found cells into rows
  return groups.map((group) => {
    const rowMap = new Map();
    
    group.forEach(([row, col]) => {
      if (!rowMap.has(row)) rowMap.set(row, []);
      
      rowMap.get(row).push(rows[row][col]);
    });

    // Sort each row by column order
    return Array.from(rowMap.entries()).map(([row, rowCells]) =>
      rowCells.sort((a, b) => {
        const colA = rows[row].findIndex((cell) => cell.id === a.id);
        const colB = rows[row].findIndex((cell) => cell.id === b.id);
        return colA - colB;
      })
    );
  });
}

export function extractInlineFont(valueHtml) {
  const s = String(valueHtml || '');

  // 1) style="font-family: Montserrat-ExtraBold"
  let m = s.match(/style\s*=\s*"(?:[^"]*?\bfont-family\s*:\s*([^;"\s]+)[^"]*)"/i);
  if (m?.[1]) return m[1];

  // 2) style="font: Montserrat-ExtraBold"
  m = s.match(/style\s*=\s*"(?:[^"]*?\bfont\s*:\s*([^;"\s]+)[^"]*)"/i);
  if (m?.[1]) return m[1];

  return null;
}

// Anchors the rotated text inside the cell's padding box for one axis. The span's layout box is
// the *unrotated* text, so an edge anchor has to be offset by half the difference between that
// box (the 50%) and the rotated bounding box the fit measured.
function angleAxisPlacement(alignment, boxSize, startPadding, endPadding, startProperty, endProperty) {
  const centered = { position: `${startProperty}:50%;`, translate: '-50%' };

  if (!boxSize) return centered;

  const round = value => Math.round(value * 100) / 100;
  const half = (boxSize - startPadding - endPadding) / 2;

  switch (alignment) {
    case 'left':
    case 'top':
      return { position: `${startProperty}:0;`, translate: `calc(${round(startPadding + half)}px - 50%)` };
    case 'right':
    case 'bottom':
      return { position: `${endProperty}:0;`, translate: `calc(50% - ${round(endPadding + half)}px)` };
    default:
      return centered;
  }
}

// Out of flow, so the rotated text cannot force the column to the width of the *unrotated*
// text (a transform does not shrink the layout box) — the cell is sized by its fitted Height /
// Min Width instead. Needs `position: relative` on the td, and `white-space: pre` so the fitted
// size does not depend on the width it is meant to determine.
export function wrapCellAngleHtml(html, angle, rules = {}) {
  const deg = parseFloat(angle);

  if (!Number.isFinite(deg) || deg === 0) return html;

  const padding = side => parseFloat(rules[`padding-${side}`]) || 0;
  const horizontal = angleAxisPlacement(
    String(rules['text-align'] || DEFAULT_CELL_HORIZONTAL_ALIGN).toLowerCase(),
    parseFloat(rules[CELL_ANGLE_FIT_WIDTH_RULE]) || 0,
    padding('left'),
    padding('right'),
    'left',
    'right',
  );
  const vertical = angleAxisPlacement(
    String(rules['vertical-align'] || DEFAULT_CELL_VERTICAL_ALIGN).toLowerCase(),
    parseFloat(rules[CELL_ANGLE_FIT_HEIGHT_RULE]) || 0,
    padding('top'),
    padding('bottom'),
    'top',
    'bottom',
  );

  return `<span style="position:absolute; ${horizontal.position} ${vertical.position} white-space:pre; transform:translate(${horizontal.translate}, ${vertical.translate}) rotate(${deg}deg);">${html}</span>`;
}

// A rotated cell's stored size was measured for one font size and one piece of text, so anything
// that changes either has to drop the markers — that is what makes the next fit run.
export function clearCellAngleFit(rules) {
  if (!rules) return;

  delete rules[CELL_ANGLE_FITTED_RULE];
  delete rules[CELL_ANGLE_FIT_WIDTH_RULE];
  delete rules[CELL_ANGLE_FIT_HEIGHT_RULE];
}

const ANGLE_FIT_INHERITED_RULES = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'line-height',
  'letter-spacing',
];

// Space the rotated cell text needs: measured unrotated in a detached probe (so it
// works while the cell is being edited and before the table is rendered), then rotated
// analytically. Returns null without a layout engine (jsdom in fabricreader) so stored
// sizes are never zeroed there.
export function measureCellAngleFit(cell, tableStyles = null) {
  const angle = parseFloat(cell?.styles?.getRule(CELL_ANGLE_STYLE_RULE));

  if (!Number.isFinite(angle) || angle === 0) return null;
  if (typeof document === 'undefined' || !document.body) return null;

  const probe = document.createElement('div');

  probe.style.cssText = 'position:absolute; left:-9999px; top:0; visibility:hidden; display:inline-block; white-space:pre;';

  ANGLE_FIT_INHERITED_RULES.forEach(rule => {
    const value = cell.styles.getRule(rule) || tableStyles?.getRule(rule);

    if (value) probe.style.setProperty(rule, String(value));
  });

  probe.innerHTML = cell.valueHtml || '';
  document.body.appendChild(probe);

  const width = probe.offsetWidth;
  const height = probe.offsetHeight;

  document.body.removeChild(probe);

  if (!width || !height) return null;

  const radians = angle * Math.PI / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const padding = side => parseFloat(cell.styles.getRule(`padding-${side}`)) || 0;

  return {
    angle,
    width: Math.ceil(width * cos + height * sin + padding('left') + padding('right')),
    height: Math.ceil(width * sin + height * cos + padding('top') + padding('bottom')),
  };
}

export function applyCellAngleFits(table, fits) {
  fits.forEach(({ id, size }) => table.applyAngleFit(id, size));
}

// Imported cells arrive with an angle but no fitted size (only the builder writes one).
// Fit them on load; the marker keeps it one-shot so a later manual Height is respected.
// `force` re-fits already fitted cells — for explicit user actions such as Auto-height,
// where the heights are being recomputed from the content anyway.
export function fitTableCellsWithAngle(table, { force = false } = {}) {
  const cells = table.rows.reduce(
    (acc, row) => [...acc, ...row.cells],
    [...table.headers, ...table.staticHeaders, ...table.staticFooters]
  );

  const fits = cells.reduce((acc, cell) => {
    const angle = cell.styles.getRule(CELL_ANGLE_STYLE_RULE);
    const fittedFor = cell.styles.getRule(CELL_ANGLE_FITTED_RULE);
    const fitted = String(fittedFor) === String(angle)
      && cell.styles.getRule(CELL_ANGLE_FIT_WIDTH_RULE)
      && cell.styles.getRule(CELL_ANGLE_FIT_HEIGHT_RULE);

    if (!angle || (!force && fitted)) return acc;

    const size = measureCellAngleFit(cell, table.styles);

    return size ? [...acc, { id: cell.id, size }] : acc;
  }, []);

  if (!fits.length) return;

  applyCellAngleFits(table, fits);

  // Non-enumerable so it never reaches the stored table JSON.
  Object.defineProperty(table, 'angleFitApplied', { value: true, configurable: true });
}

export function normalizePadding(input) {
  const clamp = (n) => Math.max(0, Math.min(80, Math.round(Number(n) || 0)));

  const parseOne = (v) => {
    if (typeof v === "number") return clamp(v);
    if (typeof v === "string") {
      const m = v.trim().match(/^(-?\d+(\.\d+)?)/);
      return clamp(m ? parseFloat(m[1]) : 0);
    }
    return 0;
  };

  if (typeof input === "number" || typeof input === "string") {
    const all = parseOne(input);
    return { top: all, right: all, bottom: all, left: all };
  }

  if (Array.isArray(input)) {
    const a = input.map(parseOne);
    if (a.length === 1) return { top: a[0], right: a[0], bottom: a[0], left: a[0] };
    if (a.length === 2) return { top: a[0], right: a[1], bottom: a[0], left: a[1] };
    if (a.length >= 4) return { top: a[0], right: a[1], bottom: a[2], left: a[3] };
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  if (input && typeof input === "object") {
    const top = parseOne(input.top ?? input.y ?? input.vertical);
    const bottom = parseOne(input.bottom ?? input.y ?? input.vertical);
    const right = parseOne(input.right ?? input.x ?? input.horizontal);
    const left = parseOne(input.left ?? input.x ?? input.horizontal);
    return { top, right, bottom, left };
  }

  return null;
}
